import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Form, Row, Col, Alert, Card, Badge } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import EditTimingLayout from '../../components/EditTimingLayout.jsx';
import './emps-timing.css';
import 'regenerator-runtime/runtime';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://backend.ems.uk.hiradentallab.com';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

export default function EditTiming() {
  const { userId } = useParams();
  const navigate = useNavigate();

  // Default timing state (used for days without override)
  const [timing, setTiming] = useState({
    checkIn: '',
    checkOut: '',
    paidBreakLimitMinutes: 0,
    unpaidBreakLimitMinutes: 0,
    countFromActualCheckIn: false,
  });

  // Day overrides state - each day: { shiftStart, shiftEnd, isWorkingDay, isActive }
  const [dayOverrides, setDayOverrides] = useState({});

  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');

  const shiftOptions = useMemo(() => [
    { value: 'Morning', label: 'Morning' },
    { value: 'Evening', label: 'Evening' },
  ], []);

  // Calculate default total hours from check-in and check-out
  const calculateTotalHours = useCallback((checkIn, checkOut) => {
    if (!checkIn || !checkOut) { setTotalHours(0); return; }
    const start = new Date(`1970-01-01T${checkIn}:00`);
    let end = new Date(`1970-01-01T${checkOut}:00`);
    if (end <= start) end.setDate(end.getDate() + 1);
    const hours = (end - start) / (1000 * 60 * 60);
    setTotalHours(hours > 0 ? parseFloat(hours.toFixed(2)) : 0);
  }, []);

  // Calculate hours for a specific day override
  const calculateDayHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(`1970-01-01T${checkIn}:00`);
    let end = new Date(`1970-01-01T${checkOut}:00`);
    if (end <= start) end.setDate(end.getDate() + 1);
    const hours = (end - start) / (1000 * 60 * 60);
    return hours > 0 ? parseFloat(hours.toFixed(2)) : 0;
  };

  // Fetch existing timing data on load
  useEffect(() => {
    const fetchTiming = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/timings/${userId}`);
        const data = response.data.timing || response.data;

        setTiming({
          checkIn: data.shiftStart || '',
          checkOut: data.shiftEnd || '',
          paidBreakLimitMinutes: data.paidBreakLimitMinutes || 0,
          unpaidBreakLimitMinutes: data.unpaidBreakLimitMinutes || 0,
          countFromActualCheckIn: data.countFromActualCheckIn || false,
        });

        // Load day overrides if they exist
        if (data.dayOverrides) {
          const loadedOverrides = {};
          Object.keys(data.dayOverrides).forEach(day => {
            const ov = data.dayOverrides[day];
            loadedOverrides[day] = {
              shiftStart: ov.shiftStart || '',
              shiftEnd: ov.shiftEnd || '',
              isWorkingDay: ov.isWorkingDay !== false,
              isActive: !!(ov.shiftStart && ov.shiftEnd),
            };
          });
          setDayOverrides(loadedOverrides);
        }

        calculateTotalHours(data.shiftStart || '', data.shiftEnd || '');
      } catch (error) {
        console.log('No existing timing → fresh');
        setTiming({
          checkIn: '', checkOut: '',
          paidBreakLimitMinutes: 0,
          unpaidBreakLimitMinutes: 0,
          countFromActualCheckIn: false,
        });
        setDayOverrides({});
        setTotalHours(0);
      } finally {
        setLoading(false);
      }
    };
    fetchTiming();
  }, [userId, calculateTotalHours]);

  // Handle default check-in/check-out change
  const handleShiftTimeChange = (e) => {
    const { name, value } = e.target;
    setTiming(prev => {
      const updated = { ...prev, [name]: value };
      calculateTotalHours(updated.checkIn, updated.checkOut);
      return updated;
    });
  };

  const handlePaidChange = (e) => {
    setTiming(prev => ({ ...prev, paidBreakLimitMinutes: parseInt(e.target.value, 10) || 0 }));
  };

  const handleUnpaidChange = (e) => {
    setTiming(prev => ({ ...prev, unpaidBreakLimitMinutes: parseInt(e.target.value, 10) || 0 }));
  };

  // Toggle count from actual check-in checkbox
  const handleCheckboxChange = (e) => {
    setTiming(prev => ({ ...prev, countFromActualCheckIn: e.target.checked }));
  };

  // Toggle day override on/off
  const handleDayToggle = (dayKey) => {
    setDayOverrides(prev => {
      const current = prev[dayKey] || { isActive: false, isWorkingDay: true, shiftStart: '', shiftEnd: '' };
      return {
        ...prev,
        [dayKey]: { ...current, isActive: !current.isActive }
      };
    });
  };

  // Toggle working day / day off for a specific day
  const handleDayWorkingToggle = (dayKey) => {
    setDayOverrides(prev => {
      const current = prev[dayKey] || { isActive: true, isWorkingDay: true, shiftStart: '', shiftEnd: '' };
      return {
        ...prev,
        [dayKey]: { ...current, isWorkingDay: !current.isWorkingDay }
      };
    });
  };

  // Update time for a specific day override
  const handleDayTimeChange = (dayKey, field, value) => {
    setDayOverrides(prev => {
      const current = prev[dayKey] || { isActive: true, isWorkingDay: true, shiftStart: '', shiftEnd: '' };
      const updated = { ...current, [field]: value };
      return { ...prev, [dayKey]: updated };
    });
  };

  // Submit form - handles both create and update
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert day overrides to backend format
    const formattedOverrides = {};
    Object.keys(dayOverrides).forEach(day => {
      const ov = dayOverrides[day];
      if (ov.isActive) {
        formattedOverrides[day] = {
          shiftStart: ov.shiftStart || timing.checkIn,
          shiftEnd: ov.shiftEnd || timing.checkOut,
          isWorkingDay: ov.isWorkingDay,
        };
      }
    });

    const payload = {
      shiftStart: timing.checkIn,
      shiftEnd: timing.checkOut,
      paidBreakLimitMinutes: timing.paidBreakLimitMinutes,
      unpaidBreakLimitMinutes: timing.unpaidBreakLimitMinutes,
      countFromActualCheckIn: timing.countFromActualCheckIn,
      dayOverrides: formattedOverrides,
    };

    try {
      const existsResponse = await axios.get(`${API_BASE_URL}/api/timings/${userId}`).catch(() => null);
      const exists = existsResponse && existsResponse.status === 200;
      let response;
      if (exists) {
        response = await axios.put(`${API_BASE_URL}/api/timings/${userId}`, payload);
      } else {
        response = await axios.post(`${API_BASE_URL}/api/timings/add/${userId}`, payload);
      }
      setAlertMessage(response.data.message || "Timing saved successfully!");
      setAlertVariant('success');
      setShowAlert(true);
      setTimeout(() => navigate('/emps-schedule'), 2500);
    } catch (error) {
      setAlertMessage(error.response?.data?.message || "Error saving timing.");
      setAlertVariant('danger');
      setShowAlert(true);
    }
  };

  if (loading) return <div className="text-center mt-5">Loading timing data...</div>;

  const selectedShift = timing.checkIn
    ? shiftOptions.find(opt => opt.value === (parseInt(timing.checkIn.split(':')[0], 10) < 12 ? 'Morning' : 'Evening'))
    : null;

  return (
    <div className='container-fluid'>
      <EditTimingLayout>
        <div className="emp-timing-container">
          {showAlert && (
            <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
              {alertMessage}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Default Timing Section */}
            <h5 className="mb-3">Default Timing (Mon - Thu)</h5>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Check-IN <span className="required-asterisk">*</span></Form.Label>
                  <Form.Control type="time" name="checkIn" value={timing.checkIn} onChange={handleShiftTimeChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Check-Out <span className="required-asterisk">*</span></Form.Label>
                  <Form.Control type="time" name="checkOut" value={timing.checkOut} onChange={handleShiftTimeChange} required />
                </Form.Group>
              </Col>
            </Row>

            <div className="mt-2 mb-3">
              <Badge bg="info">Default Hours: {totalHours}h</Badge>
              <span className="text-muted ms-2">(Used for days without override)</span>
            </div>

            <hr />

            {/* Break Limits Section */}
            <h5>Daily Break Allowances</h5>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Paid Break Limit (minutes)</Form.Label>
                  <Form.Control type="number" min="0" value={timing.paidBreakLimitMinutes} onChange={handlePaidChange} />
                  <Form.Text className="text-muted">Paid break minutes allowed per day.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Unpaid Break Limit (minutes)</Form.Label>
                  <Form.Control type="number" min="0" value={timing.unpaidBreakLimitMinutes} onChange={handleUnpaidChange} />
                  <Form.Text className="text-muted">Allowed unpaid break per day. Excess becomes "Excess Unpaid" and is always deducted.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <hr />

            {/* Hour Calculation Rule */}
            <h5>Hour Calculation Rule</h5>
            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="countFromActualCheckIn"
                    label="Calculate worked hours from actual check-in time (allow early start)"
                    checked={timing.countFromActualCheckIn}
                    onChange={handleCheckboxChange}
                  />
                  <Form.Text className="text-muted">
                    {timing.countFromActualCheckIn
                      ? "Hours will start counting from the exact time the employee checks in."
                      : "Hours will start from shift start time (default behavior)."}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <hr />

            {/* Day-Wise Overrides Section */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="m-0">Day-Wise Overrides</h5>
              <Badge bg="secondary">Optional</Badge>
            </div>
            <p className="text-muted small">
              Default timing above is used for Mon-Thu. Enable override for any day that needs different timing or day off.
            </p>

            <Row>
              {DAYS.map(({ key, label }) => {
                const ov = dayOverrides[key] || { isActive: false, isWorkingDay: true, shiftStart: '', shiftEnd: '' };
                const dayHours = ov.isActive && ov.isWorkingDay && ov.shiftStart && ov.shiftEnd
                  ? calculateDayHours(ov.shiftStart, ov.shiftEnd)
                  : null;

                return (
                  <Col md={6} lg={4} key={key} className="mb-3">
                    <Card className={ov.isActive ? "border-primary" : "border-light"}>
                      <Card.Header className="d-flex justify-content-between align-items-center py-2">
                        <strong>{label}</strong>
                        <Form.Check
                          type="switch"
                          id={`toggle-${key}`}
                          label="Override"
                          checked={ov.isActive}
                          onChange={() => handleDayToggle(key)}
                        />
                      </Card.Header>

                      {ov.isActive && (
                        <Card.Body>
                          <Form.Check
                            type="switch"
                            id={`working-${key}`}
                            label="Working Day"
                            checked={ov.isWorkingDay}
                            onChange={() => handleDayWorkingToggle(key)}
                            className="mb-3"
                          />

                          {ov.isWorkingDay ? (
                            <>
                              <Row className="g-2">
                                <Col xs={6}>
                                  <Form.Control
                                    type="time"
                                    size="sm"
                                    placeholder="Check-In"
                                    value={ov.shiftStart}
                                    onChange={(e) => handleDayTimeChange(key, 'shiftStart', e.target.value)}
                                  />
                                  <Form.Text className="text-muted">Check-In</Form.Text>
                                </Col>
                                <Col xs={6}>
                                  <Form.Control
                                    type="time"
                                    size="sm"
                                    placeholder="Check-Out"
                                    value={ov.shiftEnd}
                                    onChange={(e) => handleDayTimeChange(key, 'shiftEnd', e.target.value)}
                                  />
                                  <Form.Text className="text-muted">Check-Out</Form.Text>
                                </Col>
                              </Row>
                              {dayHours !== null && (
                                <div className="mt-2">
                                  <Badge bg="success">{dayHours}h</Badge>
                                </div>
                              )}
                            </>
                          ) : (
                            <Badge bg="danger">Day Off</Badge>
                          )}
                        </Card.Body>
                      )}

                      {!ov.isActive && (
                        <Card.Body className="py-2">
                          <small className="text-muted">Using default: {timing.checkIn || '--:--'} - {timing.checkOut || '--:--'}</small>
                        </Card.Body>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>

            <hr />

            {/* Summary & Save */}
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Default Shift Hours: {totalHours}</strong>
              </div>
              <div>
                <Form.Group>
                  <Form.Label>Selected Shift:</Form.Label>
                  <Select options={shiftOptions} value={selectedShift} isDisabled className="w-100" />
                </Form.Group>
              </div>
            </div>

            <Button variant="primary" type="submit" className="mt-4 w-100">
              Save Timing
            </Button>
          </Form>
        </div>
      </EditTimingLayout>
    </div>
  );
}